import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/store/auth';

export interface FieldMeta {
  type: 'int' | 'num' | 'str' | 'enum' | 'bool' | 'time' | 'date' | 'datetime' | 'json' | 'phone' | 'ids';
  required?: boolean; nullable?: boolean; max?: number; min?: number; values?: string[];
  ref?: string; hidden?: boolean; readOnly?: boolean;
  kind?: 'audio' | 'destination' | 'destination_string' | 'pbx_target' | 'number_list' | 'member_target' | 'paired' | 'language';
  typeField?: string; excludeTypes?: string[]; targets?: string[]; pair?: string;
  // shown only while the enum `field` has one of `values` (unset = `unset`), e.g. a queue's delivery target
  when?: { field: string; values: string[]; unset: string };
}
export interface ResourceMeta {
  // per-action rights of the signed-in user (PHASE23); writable = creatable or editable
  path: string; section: string; creatable: boolean; editable: boolean; writable: boolean; deletable: boolean; search: string[]; filters: string[];
  fields: Record<string, FieldMeta>;
  basic?: string[];      // big forms: fields shown before "Show advanced options" (required ones always)
}

export function useMeta() {
  const uid = useAuth((s) => s.user?.id);
  const rights = useAuth((s) => s.permissions?.join(','));
  return useQuery({
    queryKey: ['meta', uid, rights],
    queryFn: async () => {
      const r = await api<{ resources: ResourceMeta[] }>('/meta/resources');
      return Object.fromEntries(r.resources.map((x) => [x.path, x])) as Record<string, ResourceMeta>;
    },
    staleTime: Infinity,
  });
}

export function rowLabel(r: Record<string, any>): string {
  if (r.agent_code) return `${r.agent_code} ${r.name ?? ''}`.trim();
  // PBX numbers (extensions, groups, rooms) and voicemail boxes: the number first
  if (r.number && r.name) return `${r.number} · ${r.name}`;
  if (r.box && r.name) return `${r.box} · ${r.name}`;
  if (r.code && r.name) return `${r.code} - ${r.name}`;
  return String(r.name ?? r.gateway_name ?? r.username ?? r.digits ?? r.phone_number ?? `#${r.id}`);
}

// options for a reference field (a list of the referenced resource, first 500)
export function useRefOptions(ref: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['ref', ref],
    enabled: !!ref && enabled,
    queryFn: async () => (await api<{ rows: Record<string, any>[] }>(`/${ref}`, { query: { limit: 500 } })).rows,
    staleTime: 30_000,
  });
}
